/**
 * Krokiz — page d'attente : les vidéos en stories.
 *
 * Une seule vidéo est chargée et lue à la fois ; la suivante n'est préchargée
 * qu'à mi-lecture de la courante. Les barres avancent par `transform`, pilotées
 * par requestAnimationFrame uniquement pendant la lecture. Appui long = pause,
 * comme sur Instagram. Onglet caché = pause.
 *
 * Lecture automatique dès l'arrivée : la première vidéo porte `autoplay` (elle part
 * même avant ce script). Si le navigateur refuse (iPhone en mode économie
 * d'énergie), la lecture reprend au premier contact du visiteur avec la page.
 */
(() => {
  const zone = document.querySelector('[data-kza-stories]');
  if (!zone) return;

  const histoires = [...zone.querySelectorAll('[data-kza-histoire]')];
  const barres = [...zone.querySelectorAll('.kza-barre i')];
  const bouton = zone.querySelector('[data-kza-lecture]');
  let courant = 0;
  let enPause = false;
  let raf = 0;
  let appuiLong = 0;

  const video = (i) => histoires[i].querySelector('video');
  histoires.forEach((h, i) => { const v = video(i); v.muted = true; v.defaultMuted = true; v.playsInline = true; });
  const charger = (v) => {
    if (!v.getAttribute('src')) {
      v.src = v.dataset.src;
      v.preload = 'auto';
    }
  };

  const peindre = () => {
    const v = video(courant);
    if (v.duration) barres[courant].style.setProperty('--p', (v.currentTime / v.duration).toFixed(4));
    if (v.duration && v.currentTime / v.duration > 0.5) charger(video((courant + 1) % histoires.length));
    raf = requestAnimationFrame(peindre);
  };
  const demarrerPeinture = () => { cancelAnimationFrame(raf); raf = requestAnimationFrame(peindre); };
  const arreterPeinture = () => cancelAnimationFrame(raf);

  let bloque = false;
  const lire = () => {
    if (enPause || document.hidden) return;
    const v = video(courant);
    charger(v);
    const p = v.play();
    if (p && p.then) {
      p.then(() => { bloque = false; zone.classList.remove('est-bloquee'); })
        .catch((e) => {
          // Lecture automatique refusée : on attend le premier geste du visiteur
          if (e && e.name === 'NotAllowedError') { bloque = true; zone.classList.add('est-bloquee'); }
          else setTimeout(lire, 300); // chargement interrompu : on réessaie
        });
    }
    demarrerPeinture();
  };
  const auPremierGeste = () => { if (bloque) lire(); };
  ['pointerup', 'touchend', 'click', 'keydown'].forEach((t) =>
    window.addEventListener(t, auPremierGeste, { passive: true }));

  const majBouton = () => {
    bouton.setAttribute('aria-pressed', String(enPause));
    bouton.setAttribute('aria-label', enPause ? 'Lire' : 'Mettre en pause');
  };

  const aller = (i) => {
    const suivant = (i + histoires.length) % histoires.length;
    const ancien = video(courant);
    ancien.pause();
    arreterPeinture();
    barres.forEach((b, j) => b.style.setProperty('--p', j < suivant ? 1 : 0));
    histoires[courant].classList.remove('est-active');
    courant = suivant;
    histoires[courant].classList.add('est-active');
    const v = video(courant);
    charger(v);
    try { v.currentTime = 0; } catch (e) { /* pas encore de métadonnées */ }
    // L'ancienne vidéo revient au début une fois masquée, sans coût visible
    setTimeout(() => { try { ancien.currentTime = 0; } catch (e) { /* rien */ } }, 400);
    lire();
  };

  histoires.forEach((h, i) => {
    const v = video(i);
    v.addEventListener('ended', () => { if (i === courant) aller(courant + 1); });
  });

  zone.querySelector('[data-kza-prec]').addEventListener('click', () => { if (!appuiLong) aller(courant - 1); });
  zone.querySelector('[data-kza-suiv]').addEventListener('click', () => { if (!appuiLong) aller(courant + 1); });
  bouton.addEventListener('click', () => {
    enPause = !enPause;
    majBouton();
    if (enPause) { video(courant).pause(); arreterPeinture(); } else { lire(); }
  });

  // Appui long : pause tant que le doigt reste posé
  let minuterie = 0;
  zone.addEventListener('pointerdown', (e) => {
    if (!e.target.closest('.kza-stories__zone')) return;
    appuiLong = 0;
    minuterie = setTimeout(() => { appuiLong = 1; video(courant).pause(); arreterPeinture(); }, 220);
  });
  const relacher = () => {
    clearTimeout(minuterie);
    if (appuiLong) { lire(); setTimeout(() => { appuiLong = 0; }, 50); }
  };
  zone.addEventListener('pointerup', relacher);
  zone.addEventListener('pointercancel', relacher);
  zone.addEventListener('pointerleave', relacher);

  document.addEventListener('keydown', (e) => {
    if (e.target.closest('input, textarea')) return;
    if (e.key === 'ArrowRight') aller(courant + 1);
    if (e.key === 'ArrowLeft') aller(courant - 1);
  });
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) { video(courant).pause(); arreterPeinture(); } else { lire(); }
  });

  // Hors écran (défilement sur petit écran d'ordinateur) : on met en pause
  if ('IntersectionObserver' in window) {
    new IntersectionObserver(([e]) => {
      if (e.isIntersecting) lire(); else { video(courant).pause(); arreterPeinture(); }
    }, { threshold: 0.2 }).observe(zone);
  }

  majBouton();
  lire();
  // Filet de sécurité : certains navigateurs ne partent qu'une fois les données prêtes
  video(0).addEventListener('canplay', () => { if (courant === 0 && video(0).paused && !enPause) lire(); }, { once: true });
})();
